'use client';

import React from 'react';
import type { ProfileConfigWidget } from 'src/api/redux/profileConfiguration/types';
import { WidgetRenderer } from 'src/components/Profiles/shared/widgetRenderer';
import type { WidgetVariant } from 'src/components/Profiles/shared/widgets/registry';

/** Widget codes that always render full-width (not distributed into columns). */
const ALWAYS_FULL_WIDTH = new Set([
  'plays',
  'treating_volume_trends',
  'referral_volume_trends',
]);

/** Widget codes that render full-width only when a component override is provided. */
const FULL_WIDTH_WITH_OVERRIDE = new Set([
  'top_providers_at_site',
  'top_locations',
]);

interface WidgetGridProps {
  widgets: ProfileConfigWidget[];
  /** Number of columns on desktop. 1 = single column, 2 = round-robin deal. Default: 2. */
  columns?: 1 | 2;
  /** Widget variant passed to WidgetRenderer. Default: 'default'. */
  variant?: WidgetVariant;
  /** Component overrides passed to WidgetRenderer. */
  componentOverrides?: Record<string, React.ComponentType>;
  /** Parent label passed to WidgetRenderer (for play context). */
  parentLabel?: string;
  /** Prefix for data-testid on each widget wrapper. */
  testIdPrefix?: string;
  /** Additional className on the outer container. */
  className?: string;
  'data-testid'?: string;
}

const WidgetGrid: React.FC<WidgetGridProps> = ({
  widgets,
  columns = 2,
  variant,
  componentOverrides,
  parentLabel,
  testIdPrefix = 'widget',
  className,
  'data-testid': dataTestId,
}) => {
  if (columns === 1) {
    return (
      <div className={`flex flex-col gap-4 ${className ?? ''}`.trim()} data-testid={dataTestId}>
        {widgets.map((widget, i) => (
          <div
            key={`${widget.code}-${widget.content_key ?? i}`}
            data-testid={`${testIdPrefix}-${widget.code}`}
          >
            <WidgetRenderer
              widget={widget}
              variant={variant}
              componentOverrides={componentOverrides}
              parentLabel={parentLabel}
            />
          </div>
        ))}
      </div>
    );
  }

  // 2-column layout: split into full-width and column batches
  const elements: React.ReactNode[] = [];
  let columnBatch: ProfileConfigWidget[] = [];

  const flushColumns = () => {
    if (columnBatch.length === 0) return;
    const batch = columnBatch;
    columnBatch = [];
    const col1 = batch.filter((_, i) => i % 2 === 0);
    const col2 = batch.filter((_, i) => i % 2 === 1);

    const renderWidget = (widget: ProfileConfigWidget, i: number) => (
      <div
        key={`${widget.code}-${widget.content_key ?? i}`}
        data-testid={`${testIdPrefix}-${widget.code}`}
      >
        <WidgetRenderer
          widget={widget}
          variant={variant}
          componentOverrides={componentOverrides}
          parentLabel={parentLabel}
        />
      </div>
    );

    const batchKey = `${batch[0].code}-${batch[0].content_key ?? batch[0].id}`;
    elements.push(
      <div key={`cols-${batchKey}`} className="hidden lg:flex gap-4">
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {col1.map(renderWidget)}
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          {col2.map(renderWidget)}
        </div>
      </div>,
      <div key={`cols-mobile-${batchKey}`} className="flex flex-col gap-4 lg:hidden">
        {batch.map(renderWidget)}
      </div>
    );
  };

  for (let widgetIdx = 0; widgetIdx < widgets.length; widgetIdx++) {
    const widget = widgets[widgetIdx];
    const hasOverride = componentOverrides?.[widget.code];
    const isFullWidth = ALWAYS_FULL_WIDTH.has(widget.code) || (FULL_WIDTH_WITH_OVERRIDE.has(widget.code) && hasOverride);
    if (isFullWidth) {
      flushColumns();
      elements.push(
        <div
          key={`full-${widget.code}-${widget.content_key ?? widget.id ?? widgetIdx}`}
          data-testid={`${testIdPrefix}-${widget.code}`}
        >
          <WidgetRenderer
            widget={widget}
            variant={variant}
            componentOverrides={componentOverrides}
            parentLabel={parentLabel}
          />
        </div>
      );
    } else {
      columnBatch.push(widget);
    }
  }
  flushColumns();

  return (
    <div className={`flex flex-col gap-4 ${className ?? ''}`.trim()} data-testid={dataTestId}>
      {elements}
    </div>
  );
};

export default WidgetGrid;
